import { z } from 'zod';

import { themeColorSchema, themeSelectionSchema } from '../base';
import { websiteThemeButtonLegacySchema } from './button';
import { websiteThemeFontLegacySchema } from './font';

/**
 * The pre-versioning theme: exactly what the editor has been writing into `draft_theme_settings`
 * all along — `{ colors, fonts, buttons }` with no entity tag and no version.
 *
 * Everything is optional and `root` is passed through untouched. This schema's job is to RECOGNIZE
 * a stored theme, not to judge it: a page whose theme fails here has no colours at all after the
 * migration, which is worse than any shape it might be carrying.
 */
export const websiteThemeLegacySchema = z
    .object({
        colors: themeSelectionSchema(themeColorSchema).optional(),
        fonts: themeSelectionSchema(websiteThemeFontLegacySchema)
            .extend({ isSynchronized: z.boolean().optional() })
            .optional(),
        buttons: themeSelectionSchema(websiteThemeButtonLegacySchema).optional(),
        /** Written by the root-styles path. Recognised so a theme holding one parses; not carried into v0. */
        root: z.unknown().optional(),
    })
    // Every field being optional would otherwise make this match ANY object — a section, a widget,
    // an empty blob — and "migrated successfully into a theme with nothing in it" is the least
    // useful thing a migration can report. At least one of the three parts has to be there; a theme
    // carrying none of them has nothing to preserve anyway.
    .refine((theme) => theme.colors !== undefined || theme.fonts !== undefined || theme.buttons !== undefined, {
        error: 'not a theme: none of colors, fonts or buttons is present',
    });

export type WebsiteThemeLegacy = z.infer<typeof websiteThemeLegacySchema>;
